import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import { toast } from "react-toastify";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_download_tip_export, API_get_tip_exports, API_preview_tip_export } from "../../../constants";

// The payroll tips file. Previews the exact rows before anything downloads,
// calls out locations that are not confirmed for the range and people with
// no payroll id, and lists recent downloads so payroll can be re-sent.
// Covers every location unless narrowed to some; a location's breakdown
// opens it on that one location.

const boxStyle = {
  position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
  width: 960, maxWidth: "94vw", maxHeight: "92vh", bgcolor: "white", borderRadius: "12px",
  boxShadow: 24, display: "flex", flexDirection: "column", overflow: "hidden", outline: "none",
};
const checkboxSx = { color: "#cbd5e1", "&.Mui-checked": { color: "#6d28d9" }, p: 0.5 };

const money = (cents) => `${(Number(cents) || 0) < 0 ? "−" : ""}$${(Math.abs(Number(cents) || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const readable = (text) => {
  const [y, m, d] = String(text).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};
// A past file only compares to the current preview when it covered the same
// dates, locations, per-location ranges (a weekly preset resolves to each
// location's own week), confirmation choice, and format.
const canonical = (value) => JSON.stringify(Object.keys(value || {}).sort().map((key) => [key, value[key]]));
const sameScope = (item, preview) =>
  item.start_date === preview.start_date
  && item.end_date === preview.end_date
  && Boolean(item.included_unconfirmed) === Boolean(preview.included_unconfirmed)
  && item.export_format === preview.export_format
  && (item.preset || "") === (preview.preset || "")
  && canonical(item.store_ranges) === canonical(preview.store_ranges)
  && JSON.stringify([...(item.store_ids || [])].sort()) === JSON.stringify([...(preview.included_store_ids || [])].sort());
const slug = (text) => String(text || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
const when = (text) => (text ? new Date(text).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export default function TipsExportModal({ open, onClose, companyId, range, preset, stores = [], initialStoreIds = null }) {
  const [includeUnconfirmed, setIncludeUnconfirmed] = React.useState(false);
  // null is every location the export covers, which can include one the
  // list does not show (only an adjustment puts it in range); once picked by
  // hand, exactly the ticked ids go to the server, even all of them.
  const [storeIds, setStoreIds] = React.useState(initialStoreIds);
  const [pickingStores, setPickingStores] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const [failed, setFailed] = React.useState("");
  const [history, setHistory] = React.useState([]);
  const [downloading, setDownloading] = React.useState(false);
  const previewTicket = React.useRef(0);
  const historyTicket = React.useRef(0);

  const params = React.useMemo(
    // A weekly preset goes by name, like the close page: each location is
    // exported for its own business week, the one it confirmed.
    () => ({
      company_id: companyId, start_date: range.start, end_date: range.end, include_unconfirmed: includeUnconfirmed ? "true" : "false",
      ...(preset && preset !== "custom" ? { preset } : {}),
      ...(storeIds ? { store_ids: storeIds.join(",") } : {}),
    }),
    [companyId, range, includeUnconfirmed, preset, storeIds]
  );
  const noStores = Boolean(storeIds) && storeIds.length === 0;
  const isPicked = (storeId) => !storeIds || storeIds.includes(storeId);
  const toggleStore = (storeId) => {
    const current = storeIds || stores.map((store) => store.store_id);
    setStoreIds(current.includes(storeId) ? current.filter((id) => id !== storeId) : [...current, storeId]);
  };
  // Names need not be unique; a shared one carries its location id so each
  // checkbox says which location it is.
  const nameCounts = stores.reduce((counts, store) => ({ ...counts, [store.store_name]: (counts[store.store_name] || 0) + 1 }), {});
  const storeLabel = (store) => (nameCounts[store.store_name] > 1 ? `${store.store_name} (#${store.store_id})` : store.store_name);
  const pickedStore = storeIds?.length === 1 ? stores.find((store) => store.store_id === storeIds[0]) : null;
  const pickedName = pickedStore ? storeLabel(pickedStore) : null;
  const scopeLabel = !storeIds
    ? "All locations"
    : pickedName || `${storeIds.length} of ${stores.length} locations`;

  const loadPreview = React.useCallback(async () => {
    const ticket = ++previewTicket.current;
    setPreview(null);
    setFailed("");
    if (noStores) return;
    try {
      const res = await getRightworkAPI(API_preview_tip_export, params);
      if (ticket !== previewTicket.current) return;
      setPreview(res.data?.redux?.payload || null);
    } catch (error) {
      if (ticket === previewTicket.current) setFailed(error.response?.data?.system?.message ?? "The preview could not be loaded");
    }
  }, [params, noStores]);
  const loadHistory = React.useCallback(async () => {
    // Same ticketing as the preview: a slow answer for a company the manager
    // has since left must not land in this one's history list.
    const ticket = ++historyTicket.current;
    try {
      const res = await getRightworkAPI(API_get_tip_exports, { company_id: companyId });
      if (ticket !== historyTicket.current) return;
      setHistory(res.data?.redux?.payload?.exports || []);
    } catch (_) {
      if (ticket === historyTicket.current) setHistory([]);
    }
  }, [companyId]);

  React.useEffect(() => {
    if (!open) return;
    loadPreview();
    loadHistory();
  }, [open, loadPreview, loadHistory]);

  // The file is rebuilt server-side; the previewed digest goes along so a
  // change since the preview is refused (409) instead of silently downloaded.
  const download = async () => {
    setDownloading(true);
    try {
      const res = await getRightworkAPI(API_download_tip_export, { ...params, expected_sha256: preview?.sha256 || "" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // One location's file is named for it and the dates it was exported
      // for (a weekly preset resolves to its own week, which can differ from
      // the home one). The id keeps two locations whose names slug alike
      // from sharing a filename.
      const single = storeIds?.length === 1 ? storeIds[0] : null;
      const [fileStart, fileEnd] = (single && preview?.store_ranges?.[String(single)]) || [range.start, range.end];
      const location = single ? `${[slug(pickedStore?.store_name), single].filter(Boolean).join("_")}_` : "";
      link.setAttribute("download", `tips_${location}${fileStart}_to_${fileEnd}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Tips file downloaded", { autoClose: 3000 });
      loadHistory();
    } catch (error) {
      toast.error(error.response?.data?.system?.message ?? "The file could not be built", { autoClose: 5000 });
      if (error.response?.status === 409) loadPreview();
    } finally {
      setDownloading(false);
    }
  };

  const unconfirmed = preview?.unconfirmed || [];
  const rows = preview?.rows || [];
  // Every location in the file's scope: the ranges cover included and
  // unconfirmed locations alike, so their count is the total; failing that,
  // the distinct locations named by the rows and the unconfirmed list
  // together (an included unconfirmed location appears in both, once here).
  const namedLocations = new Set([...rows.map((row) => row.store_name), ...unconfirmed.map((store) => store.store_name)]);
  const locationTotal = Math.max(Object.keys(preview?.store_ranges || {}).length, namedLocations.size);
  const confirmedCount = Math.max(locationTotal - unconfirmed.length, 0);
  const [showUnconfirmed, setShowUnconfirmed] = React.useState(false);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={boxStyle}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Export tips file</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"><CloseIcon style={{ fontSize: 18 }} /></button>
        </div>
        <div className="overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-1">
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Range</p>
              <p className="text-sm text-slate-900">{readable(range.start)} to {readable(range.end)}</p>
              <p className="mt-0.5 text-xs text-slate-500">Pools that end inside these dates; a weekly pool is never split across two files.</p>
              {(preview?.location_ranges || []).filter((item) => item.start_date !== range.start || item.end_date !== range.end).map((item) => (
                <p key={item.store_id} className="mt-0.5 text-xs text-slate-500">{item.store_name}: {readable(item.start_date)} to {readable(item.end_date)} (its own week)</p>
              ))}
            </div>
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Format</p>
              <p className="text-sm text-slate-900">{preview?.format_label || "Payroll ID, Employee Name, Gratuity Service Charge Take Home, Tip Take Home"}</p>
              <p className="mt-0.5 text-xs text-slate-500">One row per person per location. Tip take home includes each person's share of pooled declared cash.</p>
            </div>
          </div>

          <p className="mb-1 mt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Locations</p>
          {stores.length > 1 && (
            <div className="mb-2 rounded-lg border border-slate-200 bg-white">
              <div className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                <span className="text-sm text-slate-900">{scopeLabel}</span>
                <button type="button" onClick={() => setPickingStores((prev) => !prev)} aria-expanded={pickingStores} className="text-xs font-medium text-violet-700 hover:underline">
                  {pickingStores ? "Done" : "Choose locations"}
                </button>
                {pickingStores && (
                  <>
                    <span className="flex-1" />
                    <button type="button" onClick={() => setStoreIds(null)} className="text-xs font-medium text-violet-700 hover:underline">Select all</button>
                    <button type="button" onClick={() => setStoreIds([])} className="text-xs font-medium text-violet-700 hover:underline">Clear</button>
                  </>
                )}
              </div>
              {pickingStores && (
                <ul className="grid max-h-48 grid-cols-3 gap-x-4 gap-y-0.5 overflow-y-auto border-t border-slate-100 px-2 py-2 md:grid-cols-2 sm:grid-cols-1" aria-label="Locations in the file">
                  {stores.map((store) => (
                    <li key={store.store_id}>
                      <label className="flex cursor-pointer items-center gap-1 truncate text-[13px] text-slate-700">
                        <Checkbox checked={isPicked(store.store_id)} onChange={() => toggleStore(store.store_id)} size="small" sx={checkboxSx} inputProps={{ "aria-label": storeLabel(store) }} />
                        <span className="truncate">{storeLabel(store)}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {noStores && <p className="text-sm text-slate-500">Choose at least one location.</p>}
          {preview && unconfirmed.length === 0 && (
            <p className="text-sm text-slate-700">{locationTotal === 1 ? "The location is" : `All ${locationTotal} locations are`} confirmed for this range.</p>
          )}
          {unconfirmed.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white">
              <div className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                <span className="text-sm text-slate-900">{confirmedCount} of {locationTotal} confirmed</span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-600" />{unconfirmed.length} not confirmed
                </span>
                <button type="button" onClick={() => setShowUnconfirmed((prev) => !prev)} aria-expanded={showUnconfirmed} className="text-xs font-medium text-violet-700 hover:underline">
                  {showUnconfirmed ? "Hide" : "Show"} {unconfirmed.length === 1 ? "it" : "them"}
                </button>
                <span className="flex-1" />
                <label className="inline-flex cursor-pointer items-center gap-1 text-sm text-slate-700">
                  <Checkbox checked={includeUnconfirmed} onChange={(event) => setIncludeUnconfirmed(event.target.checked)} size="small" sx={checkboxSx} inputProps={{ "aria-label": "Include unconfirmed locations" }} />
                  Include unconfirmed locations
                </label>
              </div>
              {showUnconfirmed && (
                <ul className="grid max-h-40 grid-cols-3 gap-x-4 gap-y-1 overflow-y-auto border-t border-slate-100 px-3 py-2 text-xs text-slate-700 md:grid-cols-2 sm:grid-cols-1" aria-label="Locations not confirmed">
                  {unconfirmed.map((store) => <li key={store.store_id} className="truncate">{store.store_name}</li>)}
                </ul>
              )}
              <p className="border-t border-slate-100 px-3 py-2 text-xs text-slate-500">Unconfirmed locations stay out of the file unless included; their numbers can still change until each one is confirmed.</p>
            </div>
          )}

          <p className="mb-1 mt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Preview{preview ? ` · ${preview.row_count} ${preview.row_count === 1 ? "row" : "rows"}` : ""}
          </p>
          {failed && (
            <div className="rounded-lg border border-slate-200 bg-white p-6 text-center">
              <div className="text-sm font-semibold text-slate-700">{failed}</div>
              <button type="button" onClick={loadPreview} className="mt-2 text-sm font-semibold text-violet-700 hover:text-violet-800">Try again</button>
            </div>
          )}
          {!preview && !failed && !noStores && <div className="flex justify-center py-10"><LoadSpinner /></div>}
          {preview && (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200 text-[13px]">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase text-slate-500">Payroll ID</th>
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase text-slate-500">Employee name</th>
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase text-slate-500">Location</th>
                    <th className="px-3 py-2 text-right text-xs font-bold uppercase text-slate-500">Gratuity service charge take home</th>
                    <th className="px-3 py-2 text-right text-xs font-bold uppercase text-slate-500">Tip take home</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row, index) => (
                    <tr key={`${row.payroll_id}-${row.name}-${index}`} className={row.payroll_id ? "" : "bg-amber-50"}>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-900">{row.payroll_id || <span className="text-slate-400">–</span>}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-900">
                        {row.name}
                        {!row.payroll_id && <span className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800"><span className="h-1.5 w-1.5 rounded-full bg-amber-600" />No payroll ID</span>}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-500">{row.store_name}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-900">{money(row.amounts.service_charge)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-900">{money(row.amounts.tips + row.amounts.declared_cash)}</td>
                    </tr>
                  ))}
                  {preview.row_count === 0 && (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">No rows. Confirm locations, or include unconfirmed ones above.</td></tr>
                  )}
                </tbody>
                <tfoot className="bg-slate-50">
                  <tr>
                    <td colSpan={3} className="px-3 py-2 text-xs font-semibold text-slate-700">Totals</td>
                    <td className="px-3 py-2 text-right text-xs font-semibold tabular-nums text-slate-900">{money(preview.totals.service_charge)}</td>
                    <td className="px-3 py-2 text-right text-xs font-semibold tabular-nums text-slate-900">{money(preview.totals.tips + preview.totals.declared_cash)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          {preview && preview.draft_runs_skipped > 0 && (
            <p className="mt-2 text-xs text-slate-500">{preview.draft_runs_skipped} {preview.draft_runs_skipped === 1 ? "run has" : "runs have"} been recomputed since a location was confirmed and {preview.draft_runs_skipped === 1 ? "is" : "are"} not in the file. Reopen and confirm again to include {preview.draft_runs_skipped === 1 ? "it" : "them"}.</p>
          )}
          {preview && preview.stale_drafts_skipped > 0 && (
            <p className="mt-2 text-xs text-slate-500">{preview.stale_drafts_skipped} unconfirmed {preview.stale_drafts_skipped === 1 ? "run was" : "runs were"} computed before its inputs changed and {preview.stale_drafts_skipped === 1 ? "is" : "are"} not in the file. Sync {preview.stale_drafts_skipped === 1 ? "that location" : "those locations"} from Toast, then preview again.</p>
          )}
          {preview && preview.missing_payroll_ids > 0 && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-800"><WarningAmberOutlinedIcon style={{ fontSize: 14 }} />{preview.missing_payroll_ids} {preview.missing_payroll_ids === 1 ? "person has" : "people have"} no payroll ID. Their rows export with a blank ID; add the ID on their profile to fill it.</p>
          )}
          {preview && preview.totals.declared_cash !== 0 && (
            <p className="mt-2 text-xs text-slate-500">Tip take home includes {money(preview.totals.declared_cash)} of pooled declared cash.</p>
          )}

          {history.length > 0 && (
            <>
              <p className="mb-1 mt-5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Recent files</p>
              <div className="rounded-lg border border-slate-200">
                {history.slice(0, 5).map((item) => (
                  <div key={item.uuid} className="flex items-center gap-3 border-b border-slate-100 px-3 py-2 text-[13px] last:border-b-0">
                    <span className="text-slate-900">{readable(item.start_date)} to {readable(item.end_date)}</span>
                    <span className="text-slate-500">{item.row_count} rows</span>
                    {item.included_unconfirmed && <span className="text-xs text-amber-800">included unconfirmed</span>}
                    {preview && sameScope(item, preview) && item.sha256 !== preview.sha256 && (
                      <span className="text-xs text-amber-800">data changed since</span>
                    )}
                    <span className="flex-1" />
                    <span className="text-xs text-slate-500">{item.created_by_name ? `${item.created_by_name} · ` : ""}{when(item.created_at)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3.5">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
          <LoadingButton
            variant="contained"
            loading={downloading}
            disabled={!preview || preview.row_count === 0}
            onClick={download}
            startIcon={<DownloadIcon />}
            sx={{ textTransform: "none", borderRadius: "8px", backgroundColor: "#6d28d9", "&:hover": { backgroundColor: "#5b21b6" }, fontSize: "14px", fontWeight: 500, paddingX: "12px", paddingY: "8px" }}
          >
            Download CSV
          </LoadingButton>
        </div>
      </Box>
    </Modal>
  );
}
